import React from 'react';
import { Wallet } from 'lucide-react';
import EligibleCashbackIcon from '../../images/EligibleCashback.svg';
import RedeemableCashbackIcon from '../../images/RedeemableCashback.svg';
import LifetimeCashbackIcon from '../../images/LifetimeCashback.svg';
import { currencyRounded } from '../../utils/subscriberHelpers';

// Wallet Snapshot — deliberately distinct from Credit Snapshot's gradient
// hero band: a plain, flat tile grid with tinted icon chips, so the two
// sections don't read as one repeated component.
const WalletSnapshotCard = ({ wallet }) => {
  if (!wallet) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl p-8 flex flex-col items-center gap-3">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading wallet data…</p>
      </div>
    );
  }

  if (wallet.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <div className="text-red-600 mb-2">Failed to load wallet data</div>
        <div className="text-sm text-red-500 mb-3">{wallet.error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const walletCards = [
    {
      label: 'WB (Wallet Balance)',
      value: currencyRounded(wallet?.wallet_snapshot?.balance || 0),
      icon: <Wallet className="w-4 h-4 text-blue-600" />,
      bg: 'bg-blue-50',
    },
    {
      label: 'EC (Eligible Cashback)',
      value: currencyRounded(wallet?.wallet_snapshot?.eligible_cashback || 0),
      icon: <img src={EligibleCashbackIcon} alt="Eligible Cashback" className="w-4 h-4" />,
      bg: 'bg-amber-50',
    },
    {
      label: 'RC (Redeemable Cashback)',
      value: currencyRounded(wallet?.wallet_snapshot?.redeemable_cashback || 0),
      icon: <img src={RedeemableCashbackIcon} alt="Redeemable Cashback" className="w-4 h-4" />,
      bg: 'bg-emerald-50',
    },
    {
      label: 'LC (Lifetime Cashback)',
      value: currencyRounded(wallet?.wallet_snapshot?.lifetime_cashback || 0),
      icon: <img src={LifetimeCashbackIcon} alt="Lifetime Cashback" className="w-4 h-4" />,
      bg: 'bg-purple-50',
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-gray-400" />
          Wallet Snapshot
        </h2>
        {wallet?.wallet_snapshot?.ref_code && (
          <div className="text-sm text-gray-500">
            Ref: <span className="font-mono text-gray-700">{wallet.wallet_snapshot.ref_code}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {walletCards.map((card) => (
          <div key={card.label} className="bg-white border border-gray-100 rounded-xl px-5 py-5 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center shrink-0`}>
              {card.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-gray-500 truncate mb-1.5">{card.label}</p>
              <p className="text-xl font-bold text-gray-900 leading-tight">{card.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WalletSnapshotCard;
